import React, {useEffect, useState} from "react";
import {Box, Typography} from "@mui/material";

import {explainReport} from "./reportApi";
import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";

/**
 * The statement this report compiles to.
 *
 * THE SERVER ANSWERS IT, AND THAT IS THE WHOLE DESIGN. Building a representative statement here
 * from the shelves would be a second implementation of the compiler, and a displayed statement that
 * differs from the executed one is worse than none: it is believed, and reasoned from. The compiler
 * is pure, so asking it costs a catalogue read.
 *
 * IT ANSWERS "WHICH FIELDS DO I SEE?" BETTER THAN ANY SENTENCE. The SELECT list is on screen, in
 * order, and it is exactly the shelves: every field placed, nothing else. Somebody who has met SQL
 * reads that in a second; somebody who has not can still match the names to the shelves.
 *
 * VALUES DO NOT CROSS. The statement carries %s where a filter value goes, and the count comes back
 * separately -- a filter's contents are tenant data and do not belong on a screen that gets
 * photographed into a ticket. The persona predicate is replaced by a sentence for the same reason.
 *
 * A TAB BESIDE THE RESULT, NOT A LINK ABOVE IT. It was a disclosure above the shelves, which put
 * the query and the numbers it produced on opposite ends of the page -- and reading one against the
 * other is the entire reason somebody opens it. It is a view OF this report, so it sits where the
 * table and the charts sit.
 *
 * THE LAYOUT IS THE SERVER'S TOO. Laying a statement out is one pass over the text, and doing it
 * here would be a second place that has an opinion about what the statement says.
 */

type Props = {
    /** The specification as it currently stands, or null when it would not compile. */
    query: object | null;
};

//: Only for weight on the leading word of a line. Cosmetic by construction: a line this does not
//: recognise is shown exactly as it arrived, which is why it is allowed to be a guess.
const LEADING_KEYWORD = /^(SELECT|FROM|WHERE|GROUP BY|HAVING|ORDER BY|LIMIT|LEFT JOIN|INNER JOIN|CROSS JOIN|JOIN|UNION ALL|UNION|AND|OR|ON)\b/;

function StatementLine({text}: {text: string}) {
    const indent = text.length - text.trimStart().length;
    const found = LEADING_KEYWORD.exec(text.trimStart());

    return (
        <Box component="div" sx={{whiteSpace: "pre"}}>
            {" ".repeat(indent)}
            {found ? <Box component="span" sx={{color: Blue3, fontWeight: 600}}>{found[0]}</Box> : null}
            {found ? text.trimStart().slice(found[0].length) : text.trimStart()}
        </Box>
    );
}

export default function ExplainPanel({query}: Props) {
    const [statement, setStatement] = useState<string | null>(null);
    const [bound, setBound] = useState(0);
    const [failure, setFailure] = useState<string | null>(null);
    const [busy, setBusy] = useState(true);

    // RE-READ WHENEVER THE SHELVES MOVE. A statement from two edits ago is the kind of stale that
    // looks current, and this only renders while its tab is the one being looked at.
    const settled = JSON.stringify(query);

    useEffect(() => {
        if (!query) return;
        let current = true;
        setBusy(true);
        setFailure(null);
        explainReport(query)
            .then(answer => {
                if (!current) return;
                setStatement(answer.Statement);
                setBound(answer.BoundValues);
            })
            // THE SAME REFUSAL RUNNING WOULD GIVE, which makes this the cheapest way to find out
            // why a report will not run.
            .catch((error: any) => {
                if (!current) return;
                setFailure(error?.response?.data?.Message ?? "This report could not be compiled.");
            })
            .finally(() => { if (current) setBusy(false); });
        return () => { current = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settled]);

    if (!query) return null;

    return (
        <Box sx={{border: "1px solid #E4E7EC", borderRadius: "8px", overflow: "hidden"}}>
            <Box sx={{px: 2, py: 1.25, borderBottom: "1px solid #E4E7EC", backgroundColor: "#FAFBFC"}}>
                <Typography sx={{fontSize: 12, color: Grey5}}>
                    Compiled by the server from what you arranged. Nothing here was typed, and the
                    columns are simply your shelves in order.
                </Typography>
            </Box>

            <Box sx={{p: 2, backgroundColor: "#FFFFFF"}}>
                {busy ? (
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>Compiling…</Typography>
                ) : failure ? (
                    <Typography sx={{fontSize: 12.5, color: "#B42318"}}>{failure}</Typography>
                ) : (
                    <>
                        {/* ONE LINE PER LINE, AND THE LINES ARE NOT WRAPPED. Indentation is what
                            makes a nested statement readable, and wrapping a long line into the
                            column beneath it destroys exactly that. Long lines scroll instead. */}
                        <Box sx={{m: 0, fontSize: 12.5, lineHeight: 1.7, color: Grey17,
                                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  overflowX: "auto", tabSize: 4}}>
                            {(statement ?? "").split("\n").map((line, index) => (
                                <StatementLine key={index} text={line}/>
                            ))}
                        </Box>
                        <Typography sx={{fontSize: 11.5, color: Grey6, mt: 2}}>
                            {/* SAYING WHY THE VALUES ARE ABSENT is better than leaving somebody to
                                wonder whether the filter reached the query. */}
                            {bound === 1
                                ? "One value binds where %s appears — filter values are not shown."
                                : `${bound} values bind where %s appears — filter values are not shown.`}
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    );
}
